import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    FormControlLabel,
    IconButton,
    Popover,
    Radio,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { UMInputNumber } from '@urbanmetrix/ui-kit';
import { FlipBackward, FlipForward } from '@untitledui-pro/icons/line';
import { getViewer } from '../../../store/viewerStore';
import { addObstructionFromPolygon } from '@data/geodata/edit/addObstruction';
import { addProjectSketchFromPolygon } from '@data/geodata/edit/addProjectSketch';
import { convertBuildingsToProjectSketch } from '@data/geodata/edit/convertBuildingToProjectSketch';
import { useSessionStore } from '@gui/store/session/uiSessionStore';
import { PolygonDrawer } from '../DrawModule/PolygonDrawer';
import FVector from '@data/geometry/FVector';
import { DrawModule } from '@rendering/DrawingModule/DrawingModule';
import CustomTooltip from '../generic/CustomTooltip';
import usePersistentStore from '@gui/store/persistent/persistentStore';
import { Panels } from '@gui/constants/Panels';
import { getGrid } from '../../../store/LoadingStore';
import useNotificationStore from '@gui/store/notification';
import { NotificationType } from '@visualizer/types';
import { COLUMN_WIDTH } from './settings/SidePaneDialog';
import { useCapabilities } from '../../../demo/capabilities';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';

type ContextProps = {
    showContent: boolean;
};

// ------------------------------------------------------------------
// EditGeoData
// ------------------------------------------------------------------
const EditGeodata: React.FC<ContextProps> = ({ showContent }) => {
    const viewer = getViewer();
    const selectMode = viewer.selectMode;
    const editManager = viewer.geoEditManager;
    const { canSaveGeodata } = useCapabilities();
    const env = useEnvStore((s) => s.env);

    const setLoadingMessage = useSessionStore((s) => s.setLoadingMessage);
    const setPanel = usePersistentStore((s) => s.setPanel);
    const setEditEnabled = useCallback((next: boolean) => setPanel({ editGeodataEnabled: next }, Panels.GEODATA), [setPanel]);

    const hasEditing = useMemo(() => Boolean(selectMode && editManager), [selectMode, editManager]);

    const [erase] = useState(false);
    const [saving, setSaving] = useState(false);
    const [savePopoverOpen, setSavePopoverOpen] = useState(false);
    const [newGeodataName, setNewGeodataName] = useState('Edited Geodata');
    const [activeSection, setActiveSection] = useState<'erase' | 'add' | 'convert'>('erase');
    const [opsCount, setOpsCount] = useState(() => editManager?.getOperations().length ?? 0);
    const saveButtonRef = useRef<HTMLButtonElement | null>(null);

    // Add Operation
    const [isDrawingObstruction, setIsDrawingObstruction] = useState(false);
    const [isDrawingProjectSketch, setIsDrawingProjectSketch] = useState(false);
    const [obstructionHeight, setObstructionHeight] = useState(10);
    const [obstructionHeightDraft, setObstructionHeightDraft] = useState<string | number>('10.0');
    const [obstructionHeightValid, setObstructionHeightValid] = useState(true);
    const [obstructionElevation] = useState(0); // meters
    const [flatTop] = useState(true);
    const [projectSketchHeight, setProjectSketchHeight] = useState(10);
    const [projectSketchHeightDraft, setProjectSketchHeightDraft] = useState<string | number>('10.0');
    const [projectSketchHeightValid, setProjectSketchHeightValid] = useState(true);
    const [projectSketchElevation] = useState(0); // meters
    const [projectSketchFlatTop] = useState(true);
    const isDrawing = isDrawingObstruction || isDrawingProjectSketch;

    // Draw Module (Obstruction)
    const obstructionDrawModule = useMemo(() => new DrawModule(() => {}, []), []);

    // Draw Module (Project Sketch)
    const sketchDrawModule = useMemo(() => new DrawModule(() => {}, []), []);

    const discardActivePolygons = useCallback(() => {
        setIsDrawingObstruction(false);
        setIsDrawingProjectSketch(false);
        obstructionDrawModule.stopAndDiscard();
        sketchDrawModule.stopAndDiscard();
    }, [obstructionDrawModule, sketchDrawModule]);

    const handleSectionChange = useCallback(
        (nextSection: 'erase' | 'add' | 'convert') => {
            setActiveSection(nextSection);
            if (nextSection !== 'add') {
                discardActivePolygons();
            }
        },
        [discardActivePolygons]
    );

    useEffect(() => {
        setEditEnabled(showContent);
        if (!showContent) {
            discardActivePolygons();
        }
    }, [showContent, setEditEnabled, discardActivePolygons]);

    useEffect(() => {
        if (!selectMode) return;
        if (!showContent) {
            selectMode.disable();
            return;
        }

        if (isDrawing) {
            selectMode.disable();
            return;
        }

        selectMode.setErase(erase);
        selectMode.enable();

        return () => {
            selectMode.disable();
        };
    }, [selectMode, erase, showContent, isDrawing]);

    const updateOps = useCallback(() => setOpsCount(editManager?.getOperations().length ?? 0), [editManager]);

    const isValidHeightDraft = useCallback((value: string): boolean => {
        const normalized = value.trim().replace(',', '.');
        if (!normalized || normalized.endsWith('.')) return false;
        if (!/^\d+(\.\d)?$/.test(normalized)) return false;
        const num = Number(normalized);
        return Number.isFinite(num) && num >= 1 && num <= 200;
    }, []);

    const handleTextFieldKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
    }, []);

    const handleTextFieldKeyUp = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
    }, []);

    const handleSaveGeodataCopy = useCallback(
        async (desiredName: string) => {
            if (!editManager) return;

            const normalizedName = desiredName.trim() || 'Edited Geodata';
            setSaving(true);
            setLoadingMessage({
                title: 'Saving geodata…',
                description: 'Please wait while the scene updates.',
            });

            await new Promise<void>((r) => window.requestAnimationFrame(() => r()));

            try {
                await editManager.save(undefined, normalizedName); // includes afterSave rebuild now
                updateOps();
                // Notify the user about the success in saving the geodata
                useNotificationStore.getState().showNotification('Geodata saved successfully!', NotificationType.Success, true);
            } catch (e: any) {
                console.error('Save failed', e);

                const status = e?.response?.status;
                const errBody = e?.response?.data;

                // Notify the user about the error
                useNotificationStore
                    .getState()
                    .showNotification(
                        `Failed to save geodata${status ? ` (HTTP ${status})` : ''}${errBody ? `: ${JSON.stringify(errBody)}` : ''}`,
                        NotificationType.Error,
                        true
                    );
            } finally {
                setLoadingMessage(null);
                setSaving(false);
            }
        },
        [editManager, setLoadingMessage, updateOps]
    );

    const onSaveObstruction = useCallback(
        (pts: FVector[], failed: boolean) => {
            // Always Done:
            setIsDrawingObstruction(false);
            obstructionDrawModule.dispose();

            // Return if failed:
            if (!editManager || failed) return;

            // Add Obstruction (Not failed):
            addObstructionFromPolygon({
                editManager,
                points: pts,
                height: obstructionHeight,
                elevation: obstructionElevation,
                flatTop,
            });

            setIsDrawingObstruction(false);
            updateOps();

            // Dispose of visuals & points (it has done is work):
            obstructionDrawModule.dispose();
        },
        [editManager, obstructionHeight, obstructionElevation, flatTop, obstructionDrawModule, updateOps]
    );

    useEffect(() => {
        obstructionDrawModule.setOnSaveCallback(onSaveObstruction);
    }, [obstructionDrawModule, onSaveObstruction]);

    const onSaveProjectSketch = useCallback(
        (pts: FVector[], failed: boolean) => {
            setIsDrawingProjectSketch(false);
            sketchDrawModule.dispose();

            if (!editManager || failed) return;

            addProjectSketchFromPolygon({
                editManager,
                points: pts,
                height: projectSketchHeight,
                elevation: projectSketchElevation,
                flatTop: projectSketchFlatTop,
            });

            setIsDrawingProjectSketch(false);
            updateOps();

            sketchDrawModule.dispose();
        },
        [editManager, projectSketchHeight, projectSketchElevation, projectSketchFlatTop, sketchDrawModule, updateOps]
    );

    useEffect(() => {
        sketchDrawModule.setOnSaveCallback(onSaveProjectSketch);
    }, [sketchDrawModule, onSaveProjectSketch]);

    if (!showContent) return null;

    if (!hasEditing) {
        return (
            <div>
                <p>Geo editing is not available (viewer not ready or buildings missing).</p>
            </div>
        );
    }

    const geoEditActionBtnSx = { minHeight: 32, px: 1.5, py: 0.5, width: 'fit-content', whiteSpace: 'nowrap', alignSelf: 'flex-start' };

    return (
        <div id="editGeodataPanel" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Radio
                            size="small"
                            checked={activeSection === 'erase'}
                            onChange={() => handleSectionChange('erase')}
                            sx={{ p: 0.25, mr: 0.5 }}
                        />
                    }
                    label={<Typography sx={{ fontWeight: 'bold' }}>Erase</Typography>}
                    sx={{ m: 0, ml: -0.5, width: 'fit-content', '& .MuiFormControlLabel-label': { mt: 0.125 } }}
                />
                {activeSection === 'erase' ? (
                    <>
                        <Typography variant="body2">Select objects on the map (buildings or trees) to be erased.</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                            <Button
                                id="geoEditEraseBtn"
                                onClick={() => {
                                    selectMode?.setErase(true);
                                    updateOps();
                                }}
                                disabled={isDrawing}
                                variant="contained"
                                size="small"
                                sx={geoEditActionBtnSx}
                            >
                                Erase Objects
                            </Button>
                        </Box>
                    </>
                ) : null}
            </Box>

            <Divider sx={{ mt: 0.5, mb: 0.5 }}></Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Radio
                            size="small"
                            checked={activeSection === 'add'}
                            onChange={() => handleSectionChange('add')}
                            sx={{ p: 0.25, mr: 0.5 }}
                        />
                    }
                    label={<Typography sx={{ fontWeight: 'bold' }}>Add</Typography>}
                    sx={{ m: 0, ml: -0.5, width: 'fit-content', '& .MuiFormControlLabel-label': { mt: 0.125 } }}
                />
                {activeSection === 'add' ? (
                    <>
                        <Typography variant="body2">
                            Click Draw to start. Set the object height, then draw the polygon on the map.
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                            <Button
                                id="geoEditDrawObstructionBtn"
                                onClick={() =>
                                    setIsDrawingObstruction((v) => {
                                        const next = !v;
                                        if (next) {
                                            selectMode?.disable();
                                            setIsDrawingProjectSketch(false);
                                            sketchDrawModule.stopAndDiscard();
                                        } else {
                                            obstructionDrawModule.stopAndDiscard();
                                        }
                                        return next;
                                    })
                                }
                                disabled={saving}
                                variant="contained"
                                size="small"
                                sx={geoEditActionBtnSx}
                            >
                                {isDrawingObstruction ? 'Stop Drawing Obstruction' : 'Draw Obstruction'}
                            </Button>

                            <Button
                                id="geoEditDrawProjectSketchBtn"
                                onClick={() =>
                                    setIsDrawingProjectSketch((v) => {
                                        const next = !v;
                                        if (next) {
                                            selectMode?.disable();
                                            setIsDrawingObstruction(false);
                                            obstructionDrawModule.stopAndDiscard();
                                        } else {
                                            sketchDrawModule.stopAndDiscard();
                                        }
                                        return next;
                                    })
                                }
                                disabled={saving}
                                variant="contained"
                                size="small"
                                sx={geoEditActionBtnSx}
                            >
                                {isDrawingProjectSketch ? 'Stop Drawing Project Sketch' : 'Draw Project Sketch'}
                            </Button>
                        </Box>

                        {isDrawingObstruction ? (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography sx={{ mr: 1, fontSize: 10 }}>Obstruction Height (m)</Typography>
                                <Box sx={{ ml: 'auto' }}>
                                    <UMInputNumber
                                        value={obstructionHeightDraft}
                                        defaultValue={10.0}
                                        min={1}
                                        max={200}
                                        maxDecimals={1}
                                        formatDefaultValueWithDecimals
                                        emitRawOnChange
                                        onChange={(_, value, valid) => {
                                            if (typeof value === 'string') {
                                                setObstructionHeightDraft(value);
                                                const nextValid = value.trim() === '' ? false : isValidHeightDraft(value);
                                                setObstructionHeightValid(nextValid);
                                                if (nextValid) {
                                                    setObstructionHeight(Number(value.replace(',', '.')));
                                                }
                                                return;
                                            }
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                setObstructionHeight(value);
                                                setObstructionHeightDraft(value.toFixed(1));
                                            }
                                            if (typeof valid === 'boolean') {
                                                setObstructionHeightValid(valid);
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        ) : null}

                        {isDrawingProjectSketch ? (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', width: '100%' }}>
                                <Typography sx={{ mr: 1.2, fontSize: 10 }}>Project Sketch Height (m)</Typography>
                                <Box sx={{ ml: 'auto' }}>
                                    <UMInputNumber
                                        value={projectSketchHeightDraft}
                                        defaultValue={10.0}
                                        min={1}
                                        max={200}
                                        maxDecimals={1}
                                        formatDefaultValueWithDecimals
                                        emitRawOnChange
                                        onChange={(_, value, valid) => {
                                            if (typeof value === 'string') {
                                                setProjectSketchHeightDraft(value);
                                                const nextValid = value.trim() === '' ? false : isValidHeightDraft(value);
                                                setProjectSketchHeightValid(nextValid);
                                                if (nextValid) {
                                                    setProjectSketchHeight(Number(value.replace(',', '.')));
                                                }
                                                return;
                                            }
                                            if (typeof value === 'number' && !Number.isNaN(value)) {
                                                setProjectSketchHeight(value);
                                                setProjectSketchHeightDraft(value.toFixed(1));
                                            }
                                            if (typeof valid === 'boolean') {
                                                setProjectSketchHeightValid(valid);
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        ) : null}

                        <PolygonDrawer
                            isActive={isDrawingObstruction}
                            initialPoints={[] as FVector[]}
                            drawModule={obstructionDrawModule}
                            disableSave={!obstructionHeightValid}
                        />
                        <PolygonDrawer
                            isActive={isDrawingProjectSketch}
                            initialPoints={[] as FVector[]}
                            drawModule={sketchDrawModule}
                            disableSave={!projectSketchHeightValid}
                        />
                    </>
                ) : null}
            </Box>

            <Divider sx={{ mt: 0.5, mb: 0.5 }}></Divider>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Radio
                            size="small"
                            checked={activeSection === 'convert'}
                            onChange={() => handleSectionChange('convert')}
                            sx={{ p: 0.25, mr: 0.5 }}
                        />
                    }
                    label={<Typography sx={{ fontWeight: 'bold' }}>Convert</Typography>}
                    sx={{ m: 0, ml: -0.5, width: 'fit-content', '& .MuiFormControlLabel-label': { mt: 0.125 } }}
                />
                {activeSection === 'convert' ? (
                    <>
                        <Typography variant="body2">Select buildings in the analysis area to convert them to Project Sketch.</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                            <Button
                                id="geoEditConvertToProjectSketchBtn"
                                onClick={() => {
                                    if (!editManager || !selectMode) return;
                                    const buildings = getGrid().getActiveGeodata().dataObject.Buildings;
                                    if (!buildings) return;

                                    const selection = selectMode.getSelectedBuildings();
                                    if (selection.length === 0) return;

                                    const converted = convertBuildingsToProjectSketch({
                                        editManager,
                                        buildings,
                                        selection,
                                    });

                                    if (converted > 0) {
                                        selectMode.clearSelection();
                                        updateOps();
                                    } else {
                                        useNotificationStore
                                            .getState()
                                            .showNotification(
                                                'Buildings outside analysis area are not accepted.',
                                                NotificationType.Warning
                                            );
                                    }
                                }}
                                disabled={saving || isDrawing}
                                variant="contained"
                                size="small"
                                sx={geoEditActionBtnSx}
                            >
                                Convert to Project Sketch
                            </Button>
                        </Box>
                    </>
                ) : null}
            </Box>

            <Divider sx={{ mt: 1, mb: 1 }}></Divider>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ fontSize: 10, color: 'text.primary' }}>
                    <span id="geoEditQueuedOps">Queued operations: {opsCount}</span>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CustomTooltip title="Undo" mode="hover" side="left">
                        <span>
                            <IconButton
                                id="geoEditUndoBtn"
                                onClick={() => {
                                    editManager?.undo();
                                    updateOps();
                                }}
                                size="small"
                                aria-label="Undo"
                                sx={{ color: 'primary.main', p: 0.25 }}
                            >
                                <FlipBackward className="icon-size-big" />
                            </IconButton>
                        </span>
                    </CustomTooltip>
                    <CustomTooltip title="Redo" mode="hover" side="left">
                        <span>
                            <IconButton
                                id="geoEditRedoBtn"
                                onClick={() => {
                                    editManager?.redo();
                                    updateOps();
                                }}
                                size="small"
                                aria-label="Redo"
                                sx={{ color: 'primary.main', p: 0.25 }}
                            >
                                <FlipForward className="icon-size-big" />
                            </IconButton>
                        </span>
                    </CustomTooltip>
                </Box>
            </Box>

            <Tooltip title={!canSaveGeodata ? getDisabledInEnvMessage(env) : ''}>
                <span style={{ width: '100%' }}>
                    <Button
                        id="geoEditSaveBtn"
                        ref={saveButtonRef}
                        onClick={() => {
                            setNewGeodataName('Edited Geodata');
                            setSavePopoverOpen(true);
                        }}
                        disabled={saving || !canSaveGeodata}
                        variant="contained"
                        size="small"
                        sx={{ mt: -0.5, minHeight: 32, width: '100%' }}
                    >
                        Save Geodata Copy
                        {saving && <CircularProgress size={16} sx={{ ml: 1 }} />}
                    </Button>
                </span>
            </Tooltip>

            <Popover
                open={savePopoverOpen}
                onClose={() => setSavePopoverOpen(false)}
                anchorEl={saveButtonRef.current}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        width: `${COLUMN_WIDTH - 32}px`,
                        maxWidth: `${COLUMN_WIDTH - 32}px`,
                        boxShadow: 4,
                        mb: 0.75,
                    },
                }}
            >
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        label="New Geodata name"
                        value={newGeodataName}
                        onChange={(event) => setNewGeodataName(event.target.value)}
                        onKeyDown={handleTextFieldKeyDown}
                        onKeyUp={handleTextFieldKeyUp}
                        size="small"
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="text" onClick={() => setSavePopoverOpen(false)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={saving || !newGeodataName.trim()}
                            onClick={() => {
                                setSavePopoverOpen(false);
                                void handleSaveGeodataCopy(newGeodataName);
                            }}
                        >
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </div>
    );
};

export default memo(EditGeodata);
